import { useQuery } from '@tanstack/react-query';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import useCompany from '../hooks/useCompany';
import CkModuleManagerServiceMS from '../services/CkModuleManagerServiceMS';
import { ViewModule } from '../types/api/payload/module';
import type { ModuleDetails, ModuleName } from '../types/api/response/module';
import { useAuth } from './AuthProvider';

type ModuleContextType = {
  getModule: (moduleName: ModuleName) => ModuleDetails | undefined;
  modules: ModuleDetails[] | undefined;
};

export const ModuleContext = createContext<ModuleContextType>(
  {} as ModuleContextType
);

export const useModule = () => useContext(ModuleContext);

type ModuleProviderProps = {
  children: ReactNode;
};

export default function ModuleProvider({ children }: ModuleProviderProps) {
  const { user } = useAuth();
  const { id: companyId } = useCompany(user);

  const { data: modules } = useQuery({
    enabled: !!companyId,
    queryKey: ['CkModuleManagerServiceMS.list', companyId],
    queryFn: () =>
      CkModuleManagerServiceMS.list(companyId, ViewModule.companyFeature),
  });

  const getModule = useCallback(
    (moduleName: ModuleName): ModuleDetails | undefined => {
      if (!modules?.length) return undefined;
      return modules.find((module) => module.name === moduleName);
    },
    [modules]
  );

  const value = useMemo(
    () => ({ getModule, modules }),
    [getModule, modules]
  );

  return (
    <ModuleContext.Provider value={value}>{children}</ModuleContext.Provider>
  );
}
